import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import {
  Download,
  FileText,
  Mail,
  Loader2,
  Calendar,
  User,
  Target
} from 'lucide-react';
import { ReportGenerator, ReportOptions, ReportData } from '../utils/reportGenerator';
import { ShotData } from '../types';
import { format } from 'date-fns';

interface ReportExportProps {
  shots: ShotData[];
  sessionId?: string;
  playerName?: string;
  className?: string;
  chartElements?: { [key: string]: HTMLElement | null };
}

export const ReportExport: React.FC<ReportExportProps> = ({
  shots,
  sessionId,
  playerName = 'Player',
  className,
  chartElements = {}
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'excel' | 'csv'>('pdf');
  const [emailAddress, setEmailAddress] = useState('');
  const [reportOptions, setReportOptions] = useState({
    includeCharts: true,
    includeRecommendations: true,
    includeComparison: true,
    includeSummary: true
  });
  
  const reportGenerator = useRef(new ReportGenerator());

  // Calculate statistics
  const calculateStatistics = () => {
    const totalShots = shots.length;
    const averageDistance = shots.reduce((sum, s) => sum + (s.distance || 0), 0) / totalShots;
    const averagePower = shots.reduce((sum, s) => sum + (s.powerTransfer || 0), 0) / totalShots;
    
    const qualityScores: number[] = shots.map(s => {
      switch (s.quality) {
        case 'excellent': return 100;
        case 'good': return 75;
        case 'fair': return 50;
        case 'poor': return 25;
        default: return 0;
      }
    });
    const averageQuality = qualityScores.reduce((a, b) => a + b, 0) / totalShots;
    
    const bestShot = shots.reduce((best, shot) => 
      (shot.distance || 0) > (best?.distance || 0) ? shot : best
    , shots[0]);
    
    const worstShot = shots.reduce((worst, shot) => 
      (shot.distance || 0) < (worst?.distance || 0) ? shot : worst
    , shots[0]);
    
    // Calculate improvement rate (simplified)
    const firstHalf = shots.slice(0, Math.floor(totalShots / 2));
    const secondHalf = shots.slice(Math.floor(totalShots / 2));
    const firstHalfAvg = firstHalf.reduce((sum, s) => sum + (s.distance || 0), 0) / firstHalf.length;
    const secondHalfAvg = secondHalf.reduce((sum, s) => sum + (s.distance || 0), 0) / secondHalf.length;
    const improvementRate = ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100;
    
    return {
      totalShots,
      averageDistance,
      averagePower,
      averageQuality,
      bestShot,
      worstShot,
      improvementRate
    };
  };

  // Generate recommendations based on shot data
  const generateRecommendations = (): string[] => {
    const stats = calculateStatistics();
    const recommendations: string[] = [];
    
    if (stats.averagePower < 0.6) {
      recommendations.push('Focus on increasing bat speed through strength training and technique refinement');
    }
    
    if (stats.averageDistance < 40) {
      recommendations.push('Work on timing and sweet spot contact to maximize distance');
    }
    
    const poorShots = shots.filter(s => s.quality === 'poor').length;
    if (poorShots > shots.length * 0.3) {
      recommendations.push('Practice consistency drills to reduce poor quality shots');
    }
    
    const sweetSpotIssues = shots.filter(s => (s.sweetSpotDistance || 0) > 20).length;
    if (sweetSpotIssues > shots.length * 0.4) {
      recommendations.push('Improve hand-eye coordination with targeted sweet spot drills');
    }
    
    if (stats.improvementRate < 0) {
      recommendations.push('Consider taking breaks to avoid fatigue and maintain performance');
    } else if (stats.improvementRate > 10) {
      recommendations.push('Excellent improvement trend - continue current training approach');
    }
    
    // Shot-specific recommendations
    const shotTypes = [...new Set(shots.map(s => s.shotType))];
    if (shotTypes.length < 3) {
      recommendations.push('Diversify shot selection to become a more complete batsman');
    }
    
    return recommendations;
  };

  // Capture chart images
  const captureChartImages = async (): Promise<string[]> => {
    const images: string[] = [];
    
    for (const [key, element] of Object.entries(chartElements)) {
      if (element && reportOptions.includeCharts) {
        try {
          const image = await ReportGenerator.captureElementAsImage(element);
          images.push(image);
        } catch (error) {
          console.error(`Error capturing ${key} chart:`, error);
        }
      }
    }
    
    return images;
  };

  // Generate report
  const handleGenerateReport = async () => {
    if (shots.length === 0) {
      toast({
        title: 'No Data',
        description: 'No shots available to generate report',
        variant: 'destructive'
      });
      return;
    }
    
    setIsGenerating(true);
    
    try {
      // Prepare report data
      const reportData: ReportData = {
        shots,
        statistics: calculateStatistics(),
        recommendations: reportOptions.includeRecommendations ? generateRecommendations() : [],
        chartImages: reportOptions.includeCharts ? await captureChartImages() : []
      };
      
      const options: ReportOptions = {
        title: 'Cricket Shot Analysis Report',
        playerName,
        sessionId,
        includeCharts: reportOptions.includeCharts,
        includeRecommendations: reportOptions.includeRecommendations,
        includeComparison: reportOptions.includeComparison,
        format: exportFormat
      };
      
      // Generate based on format
      if (exportFormat === 'pdf') {
        const blob = await reportGenerator.current.generatePDFReport(reportData, options);
        downloadFile(blob, `shot-analysis-${Date.now()}.pdf`, 'application/pdf');
      } else if (exportFormat === 'csv') {
        const csv = ReportGenerator.generateCSV(shots);
        const blob = new Blob([csv], { type: 'text/csv' });
        downloadFile(blob, `shot-data-${Date.now()}.csv`, 'text/csv');
      } else if (exportFormat === 'excel') {
        const excel = ReportGenerator.generateExcel(reportData);
        const blob = new Blob([excel], { type: 'text/csv' });
        downloadFile(blob, `shot-analysis-${Date.now()}.csv`, 'text/csv');
      }
      
      toast({
        title: 'Report Generated',
        description: 'Your report has been downloaded successfully',
      });
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Generation Failed',
        description: 'Failed to generate report. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Download file helper
  const downloadFile = (blob: Blob, filename: string, _type: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Send report via email
  const handleEmailReport = async () => {
    if (!emailAddress) {
      toast({
        title: 'Email Required',
        description: 'Please enter an email address',
        variant: 'destructive'
      });
      return;
    }
    
    setIsGenerating(true);
    
    try {
      // Generate report
      const reportData: ReportData = {
        shots,
        statistics: calculateStatistics(),
        recommendations: generateRecommendations(),
        chartImages: await captureChartImages()
      };
      
      // Generate report (would be sent via backend in production)
      await reportGenerator.current.generatePDFReport(reportData, {
        title: 'Cricket Shot Analysis Report',
        playerName,
        sessionId
      });

      // TODO: In production, send generated blob to backend API for email delivery
      // For now, just show success message
      toast({
        title: 'Report Sent',
        description: `Report has been sent to ${emailAddress}`,
      });
      
      setEmailAddress('');
    } catch (error) {
      console.error('Error sending report:', error);
      toast({
        title: 'Send Failed',
        description: 'Failed to send report. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Export Report
        </CardTitle>
        <CardDescription>
          Generate and export comprehensive shot analysis reports
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="download" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="download">Download</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
          </TabsList>
          
          <TabsContent value="download" className="space-y-4">
            {/* Report Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Player: {playerName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Date: {format(new Date(), 'dd/MM/yyyy')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Total Shots: {shots.length}</span>
              </div>
              {sessionId && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Session: {sessionId.slice(0, 8)}...</span>
                </div>
              )}
            </div>
            
            {/* Export Format */}
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select value={exportFormat} onValueChange={(v: string) => setExportFormat(v as 'pdf' | 'csv' | 'excel')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF Report</SelectItem>
                  <SelectItem value="csv">CSV Data</SelectItem>
                  <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Report Options */}
            <div className="space-y-3">
              <Label>Include in Report</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="summary"
                    checked={reportOptions.includeSummary}
                    onCheckedChange={(checked) => 
                      setReportOptions(prev => ({ ...prev, includeSummary: !!checked }))
                    }
                  />
                  <Label htmlFor="summary" className="text-sm font-normal">
                    Summary Statistics
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="charts"
                    checked={reportOptions.includeCharts}
                    onCheckedChange={(checked) => 
                      setReportOptions(prev => ({ ...prev, includeCharts: !!checked }))
                    }
                    disabled={exportFormat !== 'pdf'}
                  />
                  <Label htmlFor="charts" className="text-sm font-normal">
                    Performance Charts {exportFormat !== 'pdf' && '(PDF only)'}
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="recommendations"
                    checked={reportOptions.includeRecommendations}
                    onCheckedChange={(checked) => 
                      setReportOptions(prev => ({ ...prev, includeRecommendations: !!checked }))
                    }
                  />
                  <Label htmlFor="recommendations" className="text-sm font-normal">
                    Coaching Recommendations
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="comparison"
                    checked={reportOptions.includeComparison}
                    onCheckedChange={(checked) => 
                      setReportOptions(prev => ({ ...prev, includeComparison: !!checked }))
                    }
                  />
                  <Label htmlFor="comparison" className="text-sm font-normal">
                    Shot Comparisons
                  </Label>
                </div>
              </div>
            </div>
            
            {/* Generate Button */}
            <Button
              onClick={handleGenerateReport}
              disabled={isGenerating || shots.length === 0}
              className="w-full"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate & Download
                </>
              )}
            </Button>
          </TabsContent>
          
          <TabsContent value="email" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="coach@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
              />
            </div>
            
            <Button
              onClick={handleEmailReport}
              disabled={isGenerating || !emailAddress || shots.length === 0}
              className="w-full"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Report
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};